import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useHistory } from 'react-router';
import Immutable from 'immutable';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';

import {
  FormikWrapper,
  FormTextField,
  FormSelect,
  FormCheckboxes,
  FormDatePicker,
} from '@uncinc/uncinc-react-kitchen-sink';

import {
  UI_OPERATION_STATUS_REQUEST,
  userLogout,
} from '@uncinc/react-drupal-core';

import useTypedDispatch from '../../../hooks/useTypedDispatch';
import useClasses from '../../../hooks/useClasses';
import useRoutes from '../../../hooks/useRoutes';
import Page from '../../../containers/Page';
import { getEmailDomain } from '../../../helpers/email';
import useOperationStatus from '../../../hooks/useOperationStatus';
import usePopup from '../../../hooks/usePopup';
import formatDate, { stripTime } from '../../../helpers/formatDate';
import useToast from '../../../hooks/useToast';
import { openPushNotificationSettings, requestReactNativeDeleteUser } from '../../../store/actions/UI';
import {
  Button,
  CoreLink,
  CustomToast,
  Main,
  Popup,
  Table,
} from '../../../components';

import {
  DEFAULT_LANGUAGE,
  DOMAIN_ZELFHULP_ALCOHOL,
  DOMAIN_ZELFHULP_NAASTEN,
  HTI_SSO_NAME,
  IS_REACT_NATIVE,
  LANGUAGES,
  MAILTO_STERKER_NAAST,
  MULTILINGUAL,
  URL_ALLES_OVER_DRINKEN,
  URL_JELLINEK,
  USER_ROLES,
} from '../../../config/constants';

import {
  userRemoveAccount,
  userRestartProgram,
  userUpdate,
} from '../../../store/actions/User';

import './style.scss';
import { webformUnload } from '../../../store/actions/Webform';

const PageAccountSetting = () => {
  const dispatch = useTypedDispatch();
  const { t } = useTranslation();
  const { account, privacy } = useRoutes();
  const { buildClass, joinClasses } = useClasses('PageAccountSetting');
  const toast = useToast();

  const currentUserId = useSelector((state) => state.getIn(['login', 'uid'], 0));
  const user = useSelector((state) => state.getIn(['user', currentUserId], Immutable.Map()));
  const notifications = useSelector((state) => state.getIn(['user', currentUserId, 'field_notification_settings'], Immutable.List()));
  const userHasValidatedEmail = useSelector((state) => state.getIn(['user', currentUserId, 'field_validated_email', 0, 'value'], false));
  const userHasValidEmail = useSelector((state) => {
    const userEmail = state.getIn(['user', currentUserId, 'mail', 0, 'value'], '');
    return getEmailDomain(userEmail) !== 'example.com';
  });
  const userHasRoleSsoClient = useSelector((state) => {
    const roles = state.getIn(['user', currentUserId, 'roles'], Immutable.List());
    return roles.includes(USER_ROLES.SSO_CLIENT);
  });
  const userUpdateOperationStatus = useOperationStatus('user', 'update');
  const registrationsStartDate = user.getIn(['field_registrations_start_date', 0, 'value']);
  const currentDomain = useSelector((state) => state.getIn(['uiInternal', 'currentDomain', 'raw'], null));
  const siteName = useSelector((state) => state.getIn(['uiInternal', 'currentDomain', 'pretty'], null));

  const history = useHistory();

  const deletePopup = usePopup('account_delete_popup', {
    closeOnOutsideClick: true,
  });

  const restartPopup = usePopup('account_restart_popup', {
    closeOnOutsideClick: true,
    // we set closeOnRouteChange to false because the mechanism that opens this popup is
    // a corelink that adds a hash to the url. This causes the popup to close
    // since this is seen as a location change.
    //
    // We can either fine tune the implementation of the usePopup hook or add links
    // that act as buttons.
    // See https://uncinc.monday.com/boards/3375006290/pulses/5175880582
    closeOnRouteChange: false,
  });

  const restartCtaLink = useMemo(() => {
    switch (currentDomain) {
      case DOMAIN_ZELFHULP_ALCOHOL: {
        return URL_ALLES_OVER_DRINKEN;
      }

      case DOMAIN_ZELFHULP_NAASTEN: {
        return MAILTO_STERKER_NAAST;
      }

      default: {
        return URL_JELLINEK;
      }
    }
  }, [currentDomain]);

  const buildNotificationsArray = (notifications) => {
   return notifications.map((notification) => {
    return {
      value: notification
    }
   });
  };

  const onSubmit = async (values, { setSubmitting }) => {
    const { name, nickname, email, preferred_langcode, notifications, start_date } = values;

    const notificationsArray = buildNotificationsArray(notifications);

    await dispatch(userUpdate(currentUserId, {
      field_nickname: {
        value: nickname,
      },
      field_name: {
        value: name,
      },
      preferred_langcode: {
        value: preferred_langcode,
      },
      field_notification_settings: notificationsArray,
      field_registrations_start_date: {
        value: stripTime(start_date),
      },
    })).then(() => {
      toast.show(t('pages.account.settings.messages.success'), 'success');

      const hasLanguageChanged = preferred_langcode !== initialValues.preferred_langcode

      if (hasLanguageChanged) {
        dispatch(webformUnload());
        history.push(t('routes:zelfhulp.account.base', { lng: preferred_langcode }));
      }
    }).catch((err) => {
      toast.show(t('pages.account.settings.messages.error'), 'error');
      console.error(err);
    });
    setSubmitting(false);
  };

  const removeAccount = () => {
    deletePopup.close();
    if (IS_REACT_NATIVE) {
      // Redirect the user through the native app to get the push token, so it can be cleared from there
      dispatch(requestReactNativeDeleteUser());
    } else {
      dispatch(userRemoveAccount());
      dispatch(userLogout());
    }
  };

  const restartProgram = () => {
    dispatch(userRestartProgram())
      .then(() => {
        toast.show(t('pages.account.settings.confirm_restart.messages.success'), 'success');
      })
      .catch((error) => {
        toast.show(t('pages.account.settings.confirm_restart.messages.error'), 'error');
        console.error(error);
      })
      .finally(() => {
        restartPopup.close();
      });
  };

  const onPushNotificationsClicked = () => {
    dispatch(openPushNotificationSettings())
  }

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .trim()
      .required(t('pages.account.settings.fields.name.validation.required')),
    nickname: Yup.string()
      .trim()
      .optional(),
    start_date: Yup.date()
      .required()
      .max(
        new Date(registrationsStartDate),
        t('pages.account.settings.fields.registrations_start_date.validation.error'),
      )
  });

  const initialValuesNotifications = notifications.map((item) => item.get('value', ''));

  const initialValues = {
    name: user.getIn(['field_name', 0, 'value'], ''),
    nickname: user.getIn(['field_nickname', 0, 'value'], ''),
    preferred_langcode: user.getIn(['preferred_langcode', 0, 'value'], DEFAULT_LANGUAGE),
    notifications: initialValuesNotifications?.toJS() || [],
    start_date: registrationsStartDate,
  };

  return (
    <Page className={buildClass()} title={t('pages.account.settings.title')}>
      <Main className="col-md-8 col-lg-6 offset-md-2 offset-lg-1 py-8 py-lg-12">
        <h1 className={buildClass('title')}>{t('pages.account.settings.title')}</h1>

        {(userHasRoleSsoClient && !userHasValidEmail) && (
          <CustomToast
            className="mb-10"
            type="info"
            text={t('pages.account.settings.sso_info', { sso_name: HTI_SSO_NAME })}
          />
        )}

        <FormikWrapper
          initialValues={initialValues}
          enableReinitialize
          validationSchema={validationSchema}
          onSubmit={onSubmit}
          validateOnBlur={false}
          validateOnChange={false}
        >
          <div className={buildClass('user')}>
            <FormTextField
              fieldName="name"
              descriptionAboveInput
              title={t('pages.account.settings.fields.name.label')}
              placeholder={user.getIn(['name', 0, 'value'], '') || t('pages.account.settings.fields.name.placeholder')}
              description={t('pages.account.settings.fields.name.description')}
            />
            <FormTextField
              fieldName="nickname"
              descriptionAboveInput
              title={t('pages.account.settings.fields.nickname.label')}
              description={t('pages.account.settings.fields.nickname.description')}
            />
            <FormTextField
              disabled
              placeholder={user.getIn(['mail', 0, 'value'], '') || t('pages.account.settings.fields.email.placeholder')}
              fieldName="email"
              title={t('pages.account.settings.fields.email.label')}
            />
            <div className={buildClass('buttons')}>
              {/* A password is needed to changes these values, hide these page for hti users. */}
              {!userHasRoleSsoClient && (
                <Link to={account.password}>
                  {t('pages.account.change_password.title')}
                </Link>
              )}
              <Link to={account.email}>
                {t('pages.account.change_email.title')}
              </Link>
            </div>
          </div>
          <fieldset className={joinClasses(buildClass('notifications'), 'asH3')}>
            <legend>
              <h2 className={buildClass('notifications-title')}>{t('pages.account.settings.title_notifications')}</h2>
            </legend>

            <FormCheckboxes
              fieldName="notifications"
              wrapperClassName="Checkboxes--unframed"
              options={{
                daily_mail: {
                  label: t('pages.account.settings.fields.notifications.daily_mail'),
                  value: 'daily_mail',
                  disabled: !userHasValidatedEmail,
                },
                end_of_phase_mail: {
                  label: t('pages.account.settings.fields.notifications.end_of_phase_mail'),
                  value: 'end_of_phase_mail',
                  disabled: !userHasValidatedEmail,
                },
                ...(currentDomain !== DOMAIN_ZELFHULP_NAASTEN ? {
                  usage_reminder_mail: {
                    label: t('pages.account.settings.fields.notifications.usage_reminder_mail'),
                    value: 'usage_reminder_mail',
                    disabled: !userHasValidatedEmail,
                  }
                } : {}),
                forum_reply_mail: {
                  label: t('pages.account.settings.fields.notifications.forum_reply_mail'),
                  value: 'forum_reply_mail',
                  disabled: !userHasValidatedEmail,
                },
                ...(currentDomain !== DOMAIN_ZELFHULP_NAASTEN ? {
                  badge_mail: {
                    label: t('pages.account.settings.fields.notifications.badge_mail'),
                    value: 'badge_mail',
                    disabled: !userHasValidatedEmail,
                  }
                } : {}),
              }}
            />
          </fieldset>
          {MULTILINGUAL && Object.keys(LANGUAGES).length > 1 && (
            <div className={buildClass('language')}>
              <h2 className={joinClasses(buildClass('language-title'), 'visually-hidden')}>{t('pages.account.settings.title_language')}</h2>

              <FormSelect
                fieldName="preferred_langcode"
                title={t('pages.account.settings.fields.language.label')}
                options={LANGUAGES}
                isClearable={false}
                isSearchable={false}
              />

            </div>
          )}
          <div className={buildClass('start_date')}>
            <h2 className={joinClasses(buildClass('start_date-title'), 'visually-hidden')}>{t('pages.account.settings.title_start_date')}</h2>

            <FormDatePicker
              fieldName="start_date"
              title={t('pages.account.settings.fields.registrations_start_date.label')}
              isClearable={false}
              isSearchable={false}
              description={t('pages.account.settings.fields.registrations_start_date.description')}
              dateFormat="dd/MM/yyyy"
              popperPlacement="bottom-end"
              maxDate={registrationsStartDate}
            />

          </div>
          <Button
            htmlType="submit"
            className={buildClass('submit')}
            buttonState={userUpdateOperationStatus === UI_OPERATION_STATUS_REQUEST && 'disabled'}
          >
            {t('pages.account.settings.buttons.submit')}
          </Button>
          {IS_REACT_NATIVE && (
            <Button
              htmlType="button"
              className={buildClass('submit Button--secondary pushNotifications')}
              onClick={onPushNotificationsClicked}
            >
              {t('pages.account.settings.buttons.push_notifications')}
            </Button>
          )}
        </FormikWrapper>

        {deletePopup.isOpen && (
          <Popup
            title={t('pages.account.settings.confirm_delete.title')}
            closeButtonText={t('pages.account.settings.confirm_delete.cancel')}
            // eslint-disable-next-line react/no-unstable-nested-components
            cta={() => (
              <Button onClick={() => removeAccount()}>
                {t('pages.account.settings.confirm_delete.confirm')}
              </Button>
            )}
            {...deletePopup.popupProps}
          >
            <p>
              {t('pages.account.settings.confirm_delete.body')}
            </p>
          </Popup>
        )}

        {restartPopup.isOpen && (
          <Popup
            title={t('pages.account.settings.confirm_restart.title')}
            closeButtonText={t('pages.account.settings.confirm_restart.cancel')}
            // eslint-disable-next-line react/no-unstable-nested-components
            cta={() => (
              <Button
                warning
                onClick={restartProgram}
              >
                {t('pages.account.settings.confirm_restart.confirm')}
              </Button>
            )}
            {...restartPopup.popupProps}
          >
            <p>
              <Trans
                i18nKey={`pages.account.settings.confirm_restart.body.${currentDomain}`}
                values={{ link: t(`pages.account.settings.confirm_restart.links.${currentDomain}`) }}
              >
                <CoreLink to={restartCtaLink} />
              </Trans>
            </p>
          </Popup>
        )}

        <div className={buildClass('program')}>
          <h3>{t('pages.account.settings.program.title')}</h3>
          <Table className={buildClass('program-table')}>
            <Table.Row>
              <Table.Cell>
                <h4 className="asH5">{siteName}</h4>
                <span>
                  {t('pages.account.settings.program.program_started_at', {
                    date: formatDate(user.getIn(['field_program_start_date', 0, 'value'], ''), 'DD-M-YYYY'),
                  })}
                </span>
              </Table.Cell>
              <Table.Cell>
                <CoreLink
                  to={account.base}
                  title={t('buttons.restart')}
                  className="textual-link"
                  onClick={() => restartPopup.open()}
                />
              </Table.Cell>
            </Table.Row>
          </Table>
        </div>

        <Button className={joinClasses(buildClass('remove-account'), 'mt-12 mb-12')} warning onClick={() => deletePopup.open()}>
          {t('pages.account.settings.buttons.remove_account')}
        </Button>

        <Link className={buildClass('privacy')} to={privacy}>
          {t('pages.account.settings.link_privacy')}
        </Link>
      </Main>
    </Page>
  );
};

export default PageAccountSetting;
